/**
 * Frontend API client for Stripe billing endpoints (US132b).
 *
 * The frontend does **not** mount Stripe.js — it redirects the user to
 * Stripe-hosted Checkout / Billing Portal pages.
 *
 * There are two different "is Stripe available?" questions:
 *
 * 1. **Is this build configured for payments?** Determined at build
 *    time by the ``VITE_STRIPE_PUBLISHABLE_KEY`` env var (Vite inlines
 *    ``import.meta.env.VITE_*``). The ``isStripeConfigured`` helper
 *    answers this. When false, ``useUpgradeFlow`` reports
 *    ``paymentUnavailable`` so the UI can render a "Coming Soon"
 *    placeholder rather than a real "Upgrade Now" button.
 *
 * 2. **Does the backend have its secrets set?** Only the backend
 *    knows, and it answers by returning 503 from
 *    ``/billing/checkout-session``. ``useUpgradeFlow`` already
 *    handles 503 with a friendly toast and a transition to
 *    ``paymentUnavailable=true``.
 *
 * The two are independent: a build with the publishable key set can
 * still see "Coming Soon" if the backend isn't configured (we just
 * show the placeholder after the 503). And we deliberately do **not**
 * short-circuit the click handler on the build-time flag — clicking
 * "Upgrade Now" always calls the backend, so a misconfigured build
 * surfaces an actual error rather than appearing to do nothing.
 */

import api from "../api";
import type {
  BillingPortalResponse,
  CheckoutSessionRequest,
  CheckoutSessionResponse,
  InvoicesResponse,
  PricesResponse,
} from "../types/billing";

/**
 * Stripe publishable key from environment. Empty string when unset.
 * Configured via ``VITE_STRIPE_PUBLISHABLE_KEY`` in ``.env`` /
 * ``.env.development`` / deployment settings.
 *
 * The lookup is done via a direct ``import.meta.env`` reference so
 * Vite's build-time substitution can inline the value into the
 * production bundle. Previously we used ``new Function(...)`` to
 * dodge the Jest/Babel toolchain (which doesn't understand
 * ``import.meta``), but that defeats Vite's substitution: the
 * literal ``import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY`` text is
 * hidden inside a string, so Vite's parser never sees it and the
 * key never makes it into the bundle. We now guard the access with
 * a runtime try/catch so non-Vite environments (Jest) get an empty
 * string instead of a parse error.
 */
const resolvePublishableKey = (): string => {
  // Vite replaces this entire expression at build time with the
  // literal string value of VITE_STRIPE_PUBLISHABLE_KEY. In Jest
  // (no Vite plugin) the access throws, so we fall back to the
  // process.env lookup, then to an empty string.
  let fromVite = "";
  try {
    fromVite =
      (
        import.meta as ImportMeta & {
          env?: Record<string, string | undefined>;
        }
      ).env?.VITE_STRIPE_PUBLISHABLE_KEY ?? "";
  } catch {
    fromVite = "";
  }
  if (fromVite) return fromVite.trim();
  const fromProcess =
    typeof process !== "undefined"
      ? (process.env?.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined)
      : undefined;
  return fromProcess?.trim() || "";
};

export const STRIPE_PUBLISHABLE_KEY: string = resolvePublishableKey();

/** True when a publishable key is configured at build time. */
export const isStripeConfigured = (): boolean =>
  STRIPE_PUBLISHABLE_KEY.length > 0;

/**
 * Fetch the available Stripe Price combinations from the backend (US142).
 * Public endpoint — no auth required.
 */
export const getPrices = async (): Promise<PricesResponse> => {
  const response = await api.get<PricesResponse>("/billing/prices");
  return response.data;
};

/**
 * Fetch the current user's Stripe invoice history (US166).
 * Requires authentication.
 */
export const getInvoices = async (): Promise<InvoicesResponse> => {
  const response = await api.get<InvoicesResponse>("/billing/invoices");
  return response.data;
};

/**
 * Create a Stripe Checkout Session for the current user and return its
 * hosted URL. The caller is responsible for redirecting the browser to
 * ``response.url``.
 */
export const createCheckoutSession = async (
  payload: CheckoutSessionRequest = {},
): Promise<CheckoutSessionResponse> => {
  const response = await api.post<CheckoutSessionResponse>(
    "/billing/checkout-session",
    payload,
  );
  return response.data;
};

/**
 * Create a Stripe Billing Portal session for the current user. The user
 * must already have a Stripe customer id (created the first time they
 * start a checkout). The backend returns 404 in that case so the UI can
 * prompt the user to start checkout first.
 */
export const createBillingPortalSession =
  async (): Promise<BillingPortalResponse> => {
    const response = await api.post<BillingPortalResponse>(
      "/billing/portal-session",
      {},
    );
    return response.data;
  };
